package one.healthium.bland.presentation.feature.today

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Outline
import androidx.compose.ui.graphics.Paint
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawOutline
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.drawIntoCanvas
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import loco.BLAND_TODAY_MAIN_CONTROL_SECTION
import loco.GENERAL_STEPS
import loco.PCOS_TODAY_LOG_SYMPTOMS_LABEL
import loco.TODAY_ACTIVITY_LABEL
import loco.TODAY_WATER_INTAKE_LABEL
import loco.TODAY_WEIGHT_LABEL
import one.healthium.base.utils.Formatters.formatWeight
import one.healthium.base.utils.relativeDate
import one.healthium.base.utils.toVolumeString
import one.healthium.base.utils.toVolumeUnit
import one.healthium.healthKit.ui.HealthKitConnectBanner
import one.healthium.healthKit.ui.HealthKitConnectBannerContract
import one.healthium.loco.resolve
import one.healthium.meal_plans.domain.MealPlanInfoModel
import one.healthium.rateus.data.ShowAppRateForStart
import one.healthium.today.ui.TodayUiState
import one.healthium.today.ui.TodayViewModel
import one.healthium.today.ui.models.DailyStepsUiModel
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.platform.PlatformColorsEffect
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.Themes
import one.healthium.ui.compose.theme.white
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import androidx.compose.ui.tooling.preview.Preview
import org.koin.compose.viewmodel.koinViewModel

interface TodayEventDelegate {
    fun onMealPlanClicked(id: String)
    fun onLogFoodClicked()
    fun onLogWaterClicked()
    fun onLogWeightClicked()
    fun onLogSymptomsClicked()
}

@Composable
fun TodayScreen(
    eventDelegate: TodayEventDelegate
) {
    PlatformColorsEffect(lightStatusBar = true)

    val viewModel = koinViewModel<TodayViewModel>()
    val state by viewModel.state().collectAsState()

    ShowAppRateForStart()

    TodayScreenContent(
        state = state,
        onLogWaterClicked = eventDelegate::onLogWaterClicked,
        onLogWeightClicked = eventDelegate::onLogWeightClicked,
        onMealPlanClicked = { eventDelegate.onMealPlanClicked(it.id) },
        onAddFoodClicked = eventDelegate::onLogFoodClicked,
        onLogSymptomsClicked = eventDelegate::onLogSymptomsClicked,
        healthKitContract = viewModel,
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
internal fun TodayScreenContent(
    state: TodayUiState,
    onLogWaterClicked: () -> Unit,
    onLogWeightClicked: () -> Unit,
    onMealPlanClicked: (MealPlanInfoModel) -> Unit,
    onAddFoodClicked: () -> Unit,
    onLogSymptomsClicked: () -> Unit,
    healthKitContract: HealthKitConnectBannerContract,
) {
    AppScaffold(Modifier.testTag("today_screen")) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            CaloriesTracker(
                state = state,
                onAddFoodClicked = onAddFoodClicked,
                onLogSymptomsClicked = onLogSymptomsClicked,
            )
            if (state.shouldShowHKBanner) {
                Spacer(Modifier.height(24.dp))
                HealthKitConnectBanner(contract = healthKitContract)
                Spacer(Modifier.height(24.dp))
            } else {
                Spacer(Modifier.height(12.dp))
            }

            Column(
                modifier = Modifier.wrapContentWidth()
            ) {
                Section(stringResource(loco.Res.string.BLAND_TODAY_MAIN_CONTROL_SECTION))
                Row(
                    modifier = Modifier.padding(horizontal = 12.dp),
                    horizontalArrangement = Arrangement.Center,
                ) {
                    LogWeightControl(state = state, onClick = onLogWeightClicked)
                    Spacer(Modifier.width(7.dp))
                    LogWaterControl(state = state, onClick = onLogWaterClicked)
                }
                Spacer(Modifier.height(12.dp))
                Row(
                    modifier = Modifier.padding(horizontal = 12.dp),
                    horizontalArrangement = Arrangement.Center,
                ) {
                    MealPlanControl(state = state, onClick = onMealPlanClicked)
                    Spacer(Modifier.width(7.dp))
                    state.todayStepsCounter?.let { steps ->
                        StepsCounterBanner(steps)
                    } ?: LogSymptomsControl(state = state, onClick = onLogSymptomsClicked)
                }
                if (state.todayStepsCounter != null) {
                    Row(
                        modifier = Modifier.padding(horizontal = 12.dp),
                        horizontalArrangement = Arrangement.Center,
                    ) {
                        MealPlanControl(state = state, onClick = onMealPlanClicked)
                        Spacer(Modifier.width(7.dp))
                        Box(Modifier.size(tileSize))
                    }
                }
                Spacer(Modifier.height(80.dp))
            }
        }
    }
}

@Composable
private fun Section(text: String) {
    Text(
        modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
        text = text,
        style = AppTheme.textStyles.secondary_17_500,
        color = AppTheme.colors.text400,
    )
}

private const val infoLabelAlpha = 0.7f
private const val headerLabelAlpha = 0.65f

@Composable
private fun LogWaterControl(
    modifier: Modifier = Modifier,
    state: TodayUiState,
    onClick: () -> Unit,
) {
    BaseLogControl(
        modifier = modifier.testTag("log_water"),
        icon = AppIcons.icBottle,
        onClick = onClick,
    ) {
        Text(
            text = stringResource(loco.Res.string.TODAY_WATER_INTAKE_LABEL),
            style = AppTheme.textStyles.secondary_17_500,
            color = AppTheme.colors.text100.copy(alpha = headerLabelAlpha),
        )
        Spacer(Modifier.height(6.dp))
        Row {
            Text(
                modifier = Modifier.testTag("water_daily_intake"),
                text = "${state.todayWaterIntake?.currentValue?.toVolumeString(state.isMetric)}" +
                        " / " +
                        "${state.todayWaterIntake?.dailyAmount?.toVolumeString(state.isMetric)}",
                style = AppTheme.textStyles.secondary_17_400,
                color = AppTheme.colors.textInverted100,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = " ${state.isMetric.toVolumeUnit().resolve()}",
                style = AppTheme.textStyles.secondary_17_400,
                color = AppTheme.colors.textInverted100.copy(alpha = infoLabelAlpha),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
private fun LogWeightControl(
    modifier: Modifier = Modifier,
    state: TodayUiState,
    onClick: () -> Unit
) {
    var weightValue: String? = null
    var dateValue: String? = null

    val latestWeightLog = state.latestWeightLog
    if (latestWeightLog != null) {
        weightValue = latestWeightLog.currentWeight.formatWeight(state.isMetric).resolve()
        dateValue = latestWeightLog.date?.relativeDate()?.resolve().orEmpty()
    }

    BaseLogControl(
        modifier = modifier.testTag("log_weight"),
        icon = AppIcons.icFileChecked,
        onClick = onClick,
    ) {
        Text(
            text = stringResource(loco.Res.string.TODAY_WEIGHT_LABEL),
            style = AppTheme.textStyles.secondary_17_500,
            color = AppTheme.colors.text100.copy(alpha = headerLabelAlpha),
        )
        Spacer(Modifier.height(6.dp))
        FlowRow(horizontalArrangement = Arrangement.Center) {
            if (!weightValue.isNullOrBlank()) {
                Text(
                    modifier = Modifier.testTag("weight_value"),
                    text = weightValue,
                    style = AppTheme.textStyles.secondary_17_400,
                    color = AppTheme.colors.textInverted100,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
            if (!dateValue.isNullOrBlank()) {
                Text(
                    text = ", $dateValue",
                    style = AppTheme.textStyles.secondary_17_400,
                    color = AppTheme.colors.textInverted100.copy(alpha = infoLabelAlpha),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

@Composable
private fun StepsCounterBanner(steps: DailyStepsUiModel) {
    BaseLogControl(
        modifier = Modifier,
        icon = AppIcons.icSteps
    ) {
        Text(
            text = stringResource(loco.Res.string.TODAY_ACTIVITY_LABEL),
            style = AppTheme.textStyles.secondary_17_500,
            color = AppTheme.colors.text100.copy(alpha = headerLabelAlpha),
        )
        Spacer(Modifier.height(6.dp))
        FlowRow(horizontalArrangement = Arrangement.Center) {
            Text(
                text = "${steps.steps} / ${steps.goal}",
                style = AppTheme.textStyles.secondary_17_500,
                color = AppTheme.colors.textInverted100,
                overflow = TextOverflow.Ellipsis,
                maxLines = 1,
            )
            Text(
                text = " ${stringResource(loco.Res.string.GENERAL_STEPS)}",
                style = AppTheme.textStyles.secondary_17_400,
                color = AppTheme.colors.textInverted100.copy(alpha = infoLabelAlpha),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}


@Composable
private fun MealPlanControl(
    modifier: Modifier = Modifier,
    state: TodayUiState,
    onClick: (MealPlanInfoModel) -> Unit,
) {
    val mealPlan = state.mealPlan

    BaseLogControl(
        modifier = modifier,
        icon = AppIcons.icPizza,
        onClick = {
            if (mealPlan != null) {
                onClick(mealPlan)
            }
        },
    ) {
        Text(
            modifier = Modifier.testTag("current_meal_plan"),
            text = mealPlan?.name?.resolve() ?: "",
            style = AppTheme.textStyles.secondary_17_500,
            color = AppTheme.colors.text100.copy(alpha = headerLabelAlpha),
            overflow = TextOverflow.Ellipsis,
            textAlign = TextAlign.Center,
        )
    }
}

@Composable
private fun LogSymptomsControl(
    modifier: Modifier = Modifier,
    state: TodayUiState,
    onClick: () -> Unit,
) {
    BaseLogControl(
        modifier = modifier,
        icon = AppIcons.icPizza,
        onClick = onClick,
    ) {
        Text(
            modifier = Modifier.testTag("log_symptoms"),
            text = stringResource(loco.Res.string.PCOS_TODAY_LOG_SYMPTOMS_LABEL),
            style = AppTheme.textStyles.secondary_17_500,
            color = AppTheme.colors.text100.copy(alpha = headerLabelAlpha),
            overflow = TextOverflow.Ellipsis,
            textAlign = TextAlign.Center,
        )
    }
}

private val tileSize = DpSize(172.dp, 160.dp)

@Composable
private fun BaseLogControl(
    modifier: Modifier = Modifier,
    icon: DrawableResource,
    onClick: () -> Unit = {},
    content: @Composable ColumnScope.() -> Unit,
) {
    val primaryColor = AppTheme.colors.primary
    val shape = AppTheme.shapes.small
    Surface(
        modifier = modifier
            .size(tileSize)
            .clip(shape)
            .drawBehind {
                drawRoundRect(
                    color = primaryColor,
                    size = size,
                )
                drawTriangle(
                    color = Color.Black.copy(alpha = 0.2f)
                )
            },
        color = Color.Transparent,
        onClick = onClick,
    ) {
        Column(
            modifier = modifier
                .fillMaxSize()
                .padding(start = 14.dp, end = 14.dp, top = 10.dp, bottom = 8.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Icon(
                modifier = Modifier.size(40.dp),
                painter = painterResource(icon),
                tint = white,
                contentDescription = null,
            )
            Spacer(Modifier.height(20.dp))
            Column(
                modifier = Modifier.fillMaxSize(),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                content()
            }
        }
    }
}

private fun DrawScope.drawTriangle(color: Color) {
    val cornerRadius = 8.dp.toPx()
    val rect = Rect(Offset.Zero, size)
    val trianglePath = Path().apply {
        moveTo(rect.topLeft.x, rect.topLeft.y)
        lineTo(rect.topRight.x, rect.topRight.y)
        lineTo(rect.center.x, rect.center.y - cornerRadius)
        close()
    }

    drawIntoCanvas { canvas ->
        canvas.drawOutline(
            outline = Outline.Generic(trianglePath),
            paint = Paint().apply {
                this.color = color
                pathEffect = PathEffect.cornerPathEffect(cornerRadius)
            }
        )
    }
}

@Composable
@Preview
private fun TodayScreenWidthPreview() {
    AppTheme(theme = Themes.bland()) {
        TodayScreenContent(
            state = TodayUiState.preview,
            onLogWaterClicked = {},
            onLogWeightClicked = {},
            onMealPlanClicked = {},
            onAddFoodClicked = {},
            onLogSymptomsClicked = {},
            healthKitContract = object : HealthKitConnectBannerContract {
                override fun onSkipClicked() = Unit
            }
        )
    }
}
